import { signOut } from 'aws-amplify/auth';
import { nextTick } from 'vue';
import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRawMainPage,
} from 'vue-router';

import pagesMeta from '@/seo/metaTags';
import { useTracking } from '@/seo/useTracking';
import {
  resetAllPiniaStores,
  useOutcomeStore,
  useProfileStore,
} from '@/stores';
import { LOG_PREFIX, PORTAL_TYPE } from '@/types/enums';
import { metaTag } from '@/types/interfaces';

export const routes: RouteRecordRawMainPage[] = [
  {
    path: '/',
    redirect: { name: 'EligibilityRating' },
    name: 'Home',
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Credit Eligibility Rating',
      metaTags: pagesMeta.dashboardPageMeta,
    },
  },
  {
    path: '/signup-questions',
    name: 'SignupQuestions',
    component: () => import('@/views/pages/SignupQuestionsPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Signup questions',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/recap',
    name: 'Recap',
    component: () => import('@/views/pages/RecapPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Recap',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/register-step2',
    name: 'RegisterStepTwo',
    component: () => import('@/views/pages/RegisterStepTwoPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Register Step 2',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/register-verify',
    name: 'RegisterVerify',
    component: () => import('@/views/pages/RegisterVerifyPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Register verify',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/delete-account',
    name: 'DeleteAccount',
    component: () => import('@/views/pages/DeleteAccountPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.DYNAMIC,
      title: 'Credit Club - Delete account',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/delete-account/error',
    name: 'DeleteAccountError',
    component: () => import('@/views/pages/AccountDeleteErrorPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Delete account',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/locked',
    name: 'AccountLocked',
    component: () => import('@/views/pages/AccountLockedPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Account locked',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/already-registered',
    name: 'AlreadyRegisteredAccount',
    component: () => import('@/views/pages/AlreadyRegisteredAccountPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Already Registered Account',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/disabled',
    name: 'Disabled',
    component: () => import('@/views/pages/AccountDisabledPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Disabled',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/thin-file',
    name: 'ThinFile',
    component: () => import('@/views/pages/ThinFilePage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Thin File',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/negative-id',
    name: 'NegativeID',
    component: () => import('@/views/pages/NegativeIDPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Negative ID',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/reconfirm-details',
    name: 'ReconfirmDetails',
    component: () => import('@/views/pages/ReconfirmDetailsPage'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Reconfirm Details',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/reauthenticate',
    name: 'Reauthenticate',
    component: () => import('@/views/pages/ReauthenticatePage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - Re-authenticate',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/credit-report',
    name: 'CreditReport',
    component: () => import('@/views/pages/CreditReportPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Check your credit report',
      metaTags: pagesMeta.creditReportPageMeta,
    },
    children: [
      {
        path: '/check-other-credit-reports',
        name: 'OtherCreditReports',
        component: () => import('@/views/pages/OtherCreditReportsPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          title: 'Credit Club - Check other credit reports',
          metaTags: pagesMeta.otherReportsPageMeta,
        },
      },
      {
        path: '',
        name: 'CreditReportSummary',
        component: () => import('@/views/pages/credit-report/CreditReportSummaryPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'personal',
        name: 'CreditReportPersonal',
        component: () => import('@/views/pages/credit-report/CreditReportPersonalPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'personal/financial-connections/:id',
        name: 'CreditReportPersonalFinancialConnection',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPersonalFinancialConnectionPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'personal/linked-address/:id',
        name: 'CreditReportPersonalLinkedAddress',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPersonalLinkedAddressPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'personal/other-name/:id',
        name: 'CreditReportPersonalOtherName',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPersonalOtherNamePage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'accounts',
        name: 'CreditReportAccounts',
        component: () => import('@/views/pages/credit-report/CreditReportAccountsPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'accounts/details/:id',
        name: 'CreditReportAccountDetails',
        component: () => import('@/views/pages/credit-report/CreditReportAccountDetailsPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },

      {
        path: 'accounts/other/:id/statement/:trID',
        name: 'CreditReportAccountStatement',
        component: () => import('@/views/pages/credit-report/CreditReportAccountStatementPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'search-history',
        name: 'CreditReportSearchHistory',
        component: () => import('@/views/pages/credit-report/CreditReportSearchHistoryPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'search-history/:index',
        name: 'CreditReportSearchHistoryItem',
        component: () =>
          import('@/views/pages/credit-report/CreditReportSearchHistoryItemPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'public-records',
        name: 'CreditReportPublicRecords',
        component: () => import('@/views/pages/credit-report/CreditReportPublicRecordsPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'public-records/bankruptcies/:index',
        name: 'CreditReportPublicRecordsInsolvency',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPublicRecordsInsolvencyPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'public-records/judgment/:index',
        name: 'CreditReportPublicRecordsJudgment',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPublicRecordsJudgmentPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
      {
        path: 'public-records/cifas-flags/:index',
        name: 'CreditReportPublicRecordsCifasFlag',
        component: () =>
          import('@/views/pages/credit-report/CreditReportPublicRecordsCifasFlagPage.vue'),
        meta: {
          isScrollFixed: false,
          portalType: PORTAL_TYPE.PORTAL,
          metaTags: pagesMeta.creditReportPageMeta,
        },
      },
    ],
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/pages/SettingsPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Settings',
      metaTags: pagesMeta.genericPageMeta,
    },
  },
  {
    path: '/scores',
    name: 'CreditScores',
    redirect: { name: 'CreditScoresEligibilityRating' },
    component: () => import('@/views/pages/CreditScoresPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Credit Score',
      metaTags: pagesMeta.genericPageMeta,
    },
    children: [
      {
        path: 'eligibility',
        name: 'CreditScoresEligibilityRating',
        component: () => import('@/views/pages/scores/eligibility/EligibilityPage.vue'),
        meta: {
          portalType: PORTAL_TYPE.PORTAL,
          isScrollFixed: false,
          metaTags: pagesMeta.genericPageMeta,
        },
      },
      {
        path: 'credit',
        name: 'CreditScoresCreditScore',
        component: () => import('@/views/pages/scores/credit/CreditScorePage.vue'),
        meta: {
          portalType: PORTAL_TYPE.PORTAL,
          isScrollFixed: false,
          metaTags: pagesMeta.genericPageMeta,
        },
      },
      {
        path: 'affordability',
        name: 'CreditScoresAffordabilityScore',
        component: () => import('@/views/pages/scores/affordability/AffordabilityScorePage.vue'),
        meta: {
          portalType: PORTAL_TYPE.PORTAL,
          isScrollFixed: false,
          metaTags: pagesMeta.genericPageMeta,
        },
      },
      {
        path: 'affordability/mortgage',
        name: 'CreditScoresMortgageCalculator',
        component: () => import('@/views/pages/scores/mortgage/MortgagePage.vue'),
        meta: {
          portalType: PORTAL_TYPE.PORTAL,
          isScrollFixed: false,
          metaTags: pagesMeta.genericPageMeta,
        },
      },
    ],
  },
  {
    path: '/home',
    name: 'EligibilityRating',
    component: () => import('@/views/pages/scores/dashboard/EligibilityDashboard.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Credit Eligibility Rating',
      metaTags: pagesMeta.dashboardPageMeta,
    },
  },
  {
    path: '/eligibility/loans/search',
    name: 'EligibilityLoansSearchPage',
    component: () => import('@/views/pages/EligibilityLoansSearchPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Loans',
      metaTags: pagesMeta.loanEligibilitySearchPageMeta,
    },
  },
  {
    path: '/eligibility/loans/results',
    name: 'EligibilityLoansResults',
    component: () => import('@/views/pages/EligibilityLoansResultsPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Loans',
      metaTags: pagesMeta.loanEligibilityResultsPageMeta,
    },
  },
  {
    path: '/eligibility/credit_cards/results',
    name: 'EligibilityCreditCardsResults',
    component: () => import('@/views/pages/EligibilityCreditCardsResultsPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Credit cards',
      metaTags: pagesMeta.cardEligibilityResultsPageMeta,
    },
  },
  {
    path: '/eligibility/credit_cards/search',
    name: 'EligibilityCreditCardsSearch',
    component: () => import('@/views/pages/EligibilityCreditCardsSearchPage.vue'),
    meta: {
      portalType: PORTAL_TYPE.PORTAL,
      title: 'Credit Club - Credit cards',
      metaTags: pagesMeta.cardEligibilitySearchPageMeta,
    },
  },
  {
    path: '/authentication-choice',
    name: 'AuthenticationChoice',
    component: () => import('@/views/pages/otp/OtpAuthenticationChoice.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - OTP Authentication Choice',
      metaTags: pagesMeta.otpAuthenticateChoicePageMeta,
    },
  },
  {
    path: '/authenticate-passcode',
    name: 'AuthenticatePasscode',
    component: () => import('@/views/pages/otp/OtpAuthenticatePasscode.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - OTP Authenticate Passcode',
      metaTags: pagesMeta.otpAuthenticateChoicePageMeta,
    },
  },
  {
    path: '/authentication-failed',
    name: 'AuthenticationFailed',
    component: () => import('@/views/pages/otp/OtpAuthenticationFailed.vue'),
    meta: {
      portalType: PORTAL_TYPE.PRE_PORTAL,
      title: 'Credit Club - OTP Authentication Failed',
      metaTags: pagesMeta.otpAuthenticateFailedPageMeta,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from) {
    if (!to.hash && !(to.meta.isScrollFixed && from.meta.isScrollFixed)) {
      document.getElementById('app')?.scrollIntoView(true);
    }
  },
});

const redirectHandler = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const outcomeStore = useOutcomeStore();
  const profileStore = useProfileStore();

  if (outcomeStore.isLocked) {
    if (to.name !== 'AccountLocked') {
      next({ name: 'AccountLocked' });
      return;
    }
  } else if (outcomeStore.isAlreadyRegistered) {
    if (to.name !== 'AlreadyRegisteredAccount') {
      next({ name: 'AlreadyRegisteredAccount' });
      return;
    }
  } else if (outcomeStore.isThinFile) {
    if (to.name !== 'ThinFile') {
      next({ name: 'ThinFile' });
      return;
    }
  } else if (outcomeStore.isNegative) {
    if (to.name !== 'NegativeID' && to.name !== 'RegisterStepTwo') {
      next({ name: 'NegativeID' });
      return;
    }
  } else if (outcomeStore.isKbaVerificationRequired || outcomeStore.isKbaVerificationFailed) {
    if (to.name !== 'RegisterVerify') {
      next({ name: 'RegisterVerify', query: to.query });
      return;
    }
  } else if (outcomeStore.isNegativeOtp) {
    if (to.name !== 'AuthenticationFailed') {
      next({ name: 'AuthenticationFailed' });
      return;
    }
  } else if (outcomeStore.isOtpRequested) {
    if (to.name !== 'AuthenticatePasscode') {
      next({ name: 'AuthenticatePasscode', query: to.query });
      return;
    }
  } else if (outcomeStore.isOtpVerificationRequired) {
    if (to.name === 'AuthenticatePasscode') {
      next();
      return;
    }
    if (to.name !== 'AuthenticationChoice') {
      next({ name: 'AuthenticationChoice', query: to.query });
      return;
    }
  } else if (outcomeStore.userExists === false && !outcomeStore.isPositive) {
    if (to.name !== 'RegisterStepTwo') {
      next({ name: 'RegisterStepTwo' });
      return;
    }
  } else if (profileStore.isValidProfile === false) {
    if (to.name !== 'Settings') {
      next({ name: 'Settings', query: { redirect: to.fullPath } });
      return;
    }
  } else if (outcomeStore.isReconfirmationRequired) {
    if (from.name === 'ReconfirmDetails' && to.name === 'Settings') {
      next();
      return;
    }
    if (to.name !== 'ReconfirmDetails') {
      next({ name: 'ReconfirmDetails', query: { redirect: to.fullPath } });
      return;
    }
  } else if (outcomeStore.isPositive) {
    if (
      (to.meta.portalType !== PORTAL_TYPE.PORTAL &&
        to.meta.portalType !== PORTAL_TYPE.WALLET_WORKOUT) ||
      to.name === 'ReconfirmDetails'
    ) {
      next({ name: 'EligibilityRating' });
      return;
    }
  } else {
    next(false);
    return;
  }
  next();
};

const setMetaTags = (to: RouteLocationNormalized) => {
  if (to?.meta?.metaTags) {
    const pageTags = to.meta.metaTags as metaTag[];

    pageTags.forEach((tag) => {
      const htmlMeta: HTMLMetaElement | null = document.querySelector(`meta[name="${tag.name}"]`);

      if (!htmlMeta) {
        const newMetaTag = document.createElement('meta');

        newMetaTag.setAttribute('name', tag.name);
        newMetaTag.content = tag.content;
        document.getElementsByTagName('head')[0].appendChild(newMetaTag);
      } else {
        htmlMeta.content = tag.content;
      }
    });
  }
};

router.beforeEach(async (to, from, next) => {
  window.$log.info(`[${LOG_PREFIX.ROUTING}]: Redirecting to ${String(to.fullPath)} from ${String(from.fullPath)}`);

  try {
    const outcomeStore = useOutcomeStore();
    const profileStore = useProfileStore();

    setMetaTags(to);
    await outcomeStore.getExistsOutcome();

    if (outcomeStore.userExists === false) {
      if (to.name === 'SignupQuestions' || to.name === 'RegisterStepTwo' || to.name === 'Recap') {
        next();
      } else {
        next({ name: 'RegisterStepTwo' });
      }
      return;
    }

    // Allow if user is going to the delete account page
    if (to.meta.portalType === PORTAL_TYPE.DYNAMIC) {
      next();
      return;
    }

    // Only allow if user is going to delete account error page from delete account page
    if (to.name === 'DeleteAccountError' && from.name === 'DeleteAccount') {
      next();
      return;
    }

    if (outcomeStore.isPositive) {
      await profileStore.checkIsValidProfile();
      if (!profileStore.accountId) {
        await profileStore.getProfile();
      }
    }

    // Only allow if user is going to reauth page from credit report page
    if (to.name === 'Reauthenticate' && from.matched.some(record => record.name === 'CreditReport')) {
      next();
      return;
    }

    redirectHandler(to, from, next);
    return;
  } catch (error) {
    window.$log.warn(`[${LOG_PREFIX.ROUTING}]: Navigation guard failed, signing out`, { error, to: String(to.fullPath) });
    resetAllPiniaStores();
    if (import.meta.env.MODE === 'development' && import.meta.env.VITE_PROXY_ENV) {
      next();
    } else {
      await signOut().catch(() => {
        /* SUPPRESSION OK HERE */
      });
      next();
    }
  }
});

router.afterEach(async (to) => {
  await nextTick(() => {
    document.title = (to.meta.title as string) || 'Credit Club';
    const { trackAuthenticationEvent } = useTracking();
    trackAuthenticationEvent().catch((error) => {
      window.$log.warn(`[${LOG_PREFIX.ROUTING}]: trackAuthenticationEvent failed`, { error });
    });
  });
});

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

export default router;
